import React, { useState } from 'react';
import API from '../services/api';
import { PROFILE_UPDATE_FIELDS } from '../profileUpdateFields';

// Admin modal: pick which client-profile fields the assigned therapist should
// fill in, with an optional note. Used from the client profile page and the
// invoice page. `client` needs { id, client_name, therapist_owner_id,
// therapist_first_name, therapist_last_name }.
export default function ProfileUpdateRequestModal({ client, onClose, onSent }) {
  const [checked, setChecked] = useState({ diagnosis_code: true });
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const therapistName = client.therapist_first_name
    ? `${client.therapist_first_name} ${client.therapist_last_name || ''}`.trim()
    : null;
  const hasTherapist = !!client.therapist_owner_id;
  const selectedFields = Object.keys(checked).filter((k) => checked[k]);

  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const send = async () => {
    if (selectedFields.length === 0) {
      setError('Select at least one field to request.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await API.post('/admin/profile-update-requests', {
        clientId: client.id,
        fields: selectedFields,
        note: note.trim() || null,
      });
      if (onSent) onSent();
      onClose();
    } catch (err) {
      console.error('Error sending profile update request:', err);
      setError(err.response?.data?.message || 'Error sending request.');
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px' }}>Request profile update</h3>
        <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 12 }}>
          {client.client_name}
          {therapistName
            ? ` — goes to ${therapistName}`
            : ''}
        </div>

        {!hasTherapist && (
          <div style={warnStyle}>
            This client has no assigned therapist, so there's nobody to send the
            request to. Assign a therapist first.
          </div>
        )}

        <div style={{ maxHeight: '40vh', overflowY: 'auto', margin: '8px 0' }}>
          {PROFILE_UPDATE_FIELDS.map((f) => (
            <label key={f.key} style={checkRowStyle}>
              <input
                type="checkbox"
                checked={!!checked[f.key]}
                onChange={() => toggle(f.key)}
                style={{ marginRight: 8 }}
              />
              {f.label}
            </label>
          ))}
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note to the therapist (e.g. needed for an insurance invoice)"
          rows={3}
          style={noteStyle}
        />

        {error && <div style={{ color: 'var(--error-color)', fontSize: 14, marginTop: 6 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
          <button
            onClick={send}
            disabled={!hasTherapist || sending || selectedFields.length === 0}
            style={{
              ...sendBtnStyle,
              opacity: !hasTherapist || sending || selectedFields.length === 0 ? 0.5 : 1,
              cursor: !hasTherapist || sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16,
};
const boxStyle = {
  background: 'var(--white)', borderRadius: 16, padding: '20px 22px', width: '100%',
  maxWidth: 440, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};
const warnStyle = {
  background: '#fff3cd', color: '#664d03', border: '1px solid #ffe69c',
  borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 8,
};
const checkRowStyle = {
  display: 'flex', alignItems: 'center', padding: '5px 2px', fontSize: 14, cursor: 'pointer',
};
const noteStyle = {
  width: '100%', boxSizing: 'border-box', borderRadius: 8,
  border: '1px solid var(--border-color)', padding: '8px 10px', fontSize: 14,
  fontFamily: 'inherit', resize: 'vertical',
};
const cancelBtnStyle = {
  background: 'var(--surface-muted)', color: 'var(--text-color)', border: 'none',
  borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer',
};
const sendBtnStyle = {
  background: 'var(--primary-color)', color: '#fff', border: 'none',
  borderRadius: 8, padding: '9px 14px', fontWeight: 700,
};
